import React, { useState } from 'react';
import { IAST_GUIDE, ExampleWord } from './iastGuideContent';

const ExampleChip: React.FC<{ word: ExampleWord }> = ({ word }) => (
  <span className="inline-flex flex-col items-center rounded-lg border border-light bg-bg-3 px-3.5 py-2 min-w-[64px]">
    <span className="font-display text-h3 leading-none text-text-primary">{word.iast}</span>
    <span className="mt-1 text-small text-text-tertiary">{word.devanagari}</span>
  </span>
);

const PronunciationGuide: React.FC = () => {
  const [showReference, setShowReference] = useState(false);

  return (
    <main id="main" className="mx-auto max-w-3xl px-4 sm:px-6 py-12 sm:py-16">
      {/* Big idea */}
      <h1 className="text-h1 font-display font-semibold mb-5">{IAST_GUIDE.title}</h1>
      <div className="space-y-3 text-body-lg text-text-secondary leading-relaxed mb-10">
        {IAST_GUIDE.bigIdea.map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>

      {/* Start here */}
      <div className="space-y-4 mb-12">
        {IAST_GUIDE.startHere.map((item, i) => (
          <div key={i} className="rounded-xl border border-light bg-bg-2 p-5">
            <h2 className="font-display text-h3 font-semibold text-accent-gold mb-1">
              {item.heading}
            </h2>
            <p className="text-body text-text-secondary leading-relaxed">{item.body}</p>
            {item.example && (
              <div className="mt-3">
                <ExampleChip word={item.example} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Rule cards */}
      <div className="space-y-5 mb-12">
        {IAST_GUIDE.cards.map((card) => (
          <div key={card.id} className="rounded-xl border border-light bg-bg-1 p-5 sm:p-6">
            <div className="flex flex-wrap items-center gap-3 mb-2.5">
              <span className="font-display text-h3 font-semibold rounded-lg bg-accent-gold/15 text-accent-gold px-3 py-1.5 tracking-wide">
                {card.markLabel}
              </span>
              <h3 className="text-h3 font-semibold text-text-primary">{card.title}</h3>
            </div>
            <p className="text-body text-text-secondary leading-relaxed">{card.rule}</p>
            {card.note && (
              <p className="mt-2 text-small italic text-text-tertiary leading-relaxed">
                {card.note}
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-2.5">
              {card.examples.map((ex) => (
                <ExampleChip key={ex.iast} word={ex} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Surprises */}
      <h2 className="text-h2 font-display font-semibold mb-4">{IAST_GUIDE.surprisesHeading}</h2>
      <div className="space-y-3 mb-12">
        {IAST_GUIDE.surprises.map((item, i) => (
          <div key={i} className="rounded-xl border border-light bg-bg-1 p-5">
            <div className="font-display text-h3 font-semibold text-accent-gold mb-1 tracking-wide">
              {item.letters}
            </div>
            <p className="text-body text-text-secondary leading-relaxed">{item.text}</p>
          </div>
        ))}
      </div>

      {/* Collapsible full reference */}
      <button
        type="button"
        onClick={() => setShowReference((v) => !v)}
        aria-expanded={showReference}
        className="w-full flex items-center justify-between rounded-xl border border-light bg-bg-3 px-5 py-4 text-left text-text-primary hover:border-strong transition-colors"
      >
        <span className="text-body-lg font-medium">{IAST_GUIDE.reference.heading}</span>
        <ChevronIcon open={showReference} />
      </button>

      {showReference && (
        <div className="mt-4">
          <h3 className="text-h3 font-display font-semibold text-accent-gold mb-2">Vowels</h3>
          <div className="rounded-xl border border-light overflow-hidden mb-6">
            {IAST_GUIDE.reference.vowels.map((row, i) => (
              <div
                key={row.letters}
                className={`flex items-center px-4 py-2.5 ${
                  i > 0 ? 'border-t border-light' : ''
                }`}
              >
                <span className="font-display text-h3 text-text-primary w-24 shrink-0">
                  {row.letters}
                </span>
                <span className="text-body text-text-secondary">{row.sound}</span>
              </div>
            ))}
          </div>

          {IAST_GUIDE.reference.consonants.map((group) => (
            <div key={group.label} className="mb-6">
              <h3 className="text-h3 font-display font-semibold text-accent-gold mb-2">
                {group.label}
              </h3>
              <div className="rounded-xl border border-light overflow-hidden">
                {group.rows.map((row, i) => (
                  <div
                    key={row.letters}
                    className={`flex items-center px-4 py-2.5 ${
                      i > 0 ? 'border-t border-light' : ''
                    }`}
                  >
                    <span className="font-display text-h3 text-text-primary w-24 shrink-0">
                      {row.letters}
                    </span>
                    <span className="text-body text-text-secondary">{row.sound}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <p className="text-small italic text-text-tertiary leading-relaxed">
            {IAST_GUIDE.reference.footnote}
          </p>
        </div>
      )}
    </main>
  );
};

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`transition-transform text-text-secondary ${open ? 'rotate-180' : ''}`}
    aria-hidden
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export default PronunciationGuide;
